import React, { useState, useEffect } from 'react';
import { FaTruck, FaSearch, FaFilter, FaEye, FaEdit, FaTrash, FaPlus, FaSpinner, FaUser, FaRoute } from 'react-icons/fa';
import { toast } from 'react-toastify';
import jobService from '../../api/jobService';
import JobFormModal from '../../components/admin/JobFormModal';
import AssignJobModal from '../../components/admin/AssignJobModal';

const AdminPredefinedJobs = () => {
  const [predefinedJobs, setPredefinedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [assigningJob, setAssigningJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);

  useEffect(() => {
    fetchPredefinedJobs();
  }, [currentPage, searchTerm]);

  const fetchPredefinedJobs = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching predefined jobs with:', { currentPage, searchTerm });
      
      const response = await jobService.getAllPredefinedJobs(currentPage, 20, searchTerm);
      console.log('📡 Predefined jobs API response:', response);
      
      if (response.success) {
        const jobs = response.data?.jobs || [];
        const pagination = response.data?.pagination || {};
        
        console.log('📊 Jobs data:', jobs);
        console.log('📊 Pagination data:', pagination);
        
        setPredefinedJobs(jobs);
        setTotalPages(pagination.pages || 1);
        setTotalJobs(pagination.total || 0);
        
        console.log('✅ State updated:', { 
          jobsCount: jobs.length, 
          totalPages: pagination.pages, 
          totalJobs: pagination.total 
        });
      } else {
        console.error('❌ Failed to fetch predefined jobs:', response.message);
        toast.error(response.message || 'Failed to fetch predefined jobs');
        setPredefinedJobs([]);
        setTotalPages(1);
        setTotalJobs(0);
      }
    } catch (error) {
      console.error('💥 Error fetching predefined jobs:', error);
      toast.error('Failed to fetch predefined jobs');
      setPredefinedJobs([]);
      setTotalPages(1);
      setTotalJobs(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const response = await jobService.getAvailableDrivers();
      if (response.success) {
        const drivers = response.data?.drivers || [];
        setAvailableDrivers(drivers);
      } else {
        console.error('Failed to fetch available drivers:', response.message);
        toast.error(response.message || 'Failed to fetch available drivers');
        setAvailableDrivers([]);
      }
    } catch (error) {
      console.error('Error fetching available drivers:', error);
      toast.error('Failed to fetch available drivers');
      setAvailableDrivers([]);
    }
  };

  const handleCreateJob = async (jobData) => {
    try {
      const response = await jobService.createPredefinedJob(jobData);
      if (response.success) {
        toast.success('Predefined job created successfully!');
        setShowCreateModal(false);
        fetchPredefinedJobs();
      } else {
        toast.error(response.message || 'Failed to create predefined job');
      }
    } catch (error) {
      console.error('Error creating predefined job:', error);
      toast.error('Failed to create predefined job');
    }
  };

  const handleUpdateJob = async (jobData) => {
    try {
      if (!selectedJob?.id) {
        toast.error('No job selected for update');
        return;
      }
      
      const response = await jobService.updatePredefinedJob(selectedJob.id, jobData);
      if (response.success) {
        toast.success('Predefined job updated successfully!');
        setShowEditModal(false);
        setSelectedJob(null);
        fetchPredefinedJobs();
      } else {
        toast.error(response.message || 'Failed to update predefined job');
      }
    } catch (error) {
      console.error('Error updating predefined job:', error);
      toast.error('Failed to update predefined job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!jobId) {
      toast.error('Invalid job ID');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this predefined job?')) {
      try {
        const response = await jobService.deletePredefinedJob(jobId);
        if (response.success) {
          toast.success('Predefined job deleted successfully!');
          fetchPredefinedJobs();
        } else {
          toast.error(response.message || 'Failed to delete predefined job');
        }
      } catch (error) {
        console.error('Error deleting predefined job:', error);
        toast.error('Failed to delete predefined job');
      }
    }
  };

  const handleOpenAssignModal = (job) => {
    if (!job) {
      toast.error('Invalid job data');
      return;
    }
    
    setSelectedJob(job);
    setShowAssignModal(true);
    fetchAvailableDrivers();
  };

  const handleAssignJob = async (jobId, driverId) => {
    if (!jobId || !driverId) {
      toast.error('Invalid job or driver ID');
      return;
    }
    
    try {
      setAssigningJob(jobId);
      const response = await jobService.assignPredefinedJob(jobId, driverId);
      if (response.success) {
        const job = predefinedJobs.find(j => j?.id === jobId);
        const driver = availableDrivers.find(d => d?.id === driverId);
        
        if (!job) {
          toast.error('Job not found in predefined jobs list');
          return;
        }
        
        const driverName = driver ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || 'Driver' : 'Driver';
        const jobRoute = job.route || `${job.pickupLocation || 'Unknown'} → ${job.deliveryLocation || 'Unknown'}`;
        
        toast.success(
          `✅ Predefined job successfully assigned to ${driverName}! Route: ${jobRoute}`,
          { autoClose: 5000, position: "top-right", hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true }
        );
        
        setShowAssignModal(false);
        setSelectedJob(null);
        fetchPredefinedJobs();
      } else {
        toast.error(response.message || 'Failed to assign predefined job');
      }
    } catch (error) {
      console.error('Error assigning predefined job:', error);
      toast.error('Failed to assign predefined job. Please try again.');
    } finally {
      setAssigningJob(null);
    }
  };

  const handleEditJob = (job) => {
    if (!job) {
      toast.error('Invalid job data');
      return;
    }
    
    setSelectedJob(job);
    setShowEditModal(true);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatRoute = (job) => {
    if (!job) return 'N/A';
    
    if (job.route) return job.route;
    
    const pickup = job.pickupLocation || 'Unknown pickup';
    const delivery = job.deliveryLocation || 'Unknown delivery';
    
    if (job.intermediateStops && Array.isArray(job.intermediateStops) && job.intermediateStops.length > 0) {
      const stops = job.intermediateStops
        .filter(stop => stop && stop.storeNumber)
        .map(stop => `Store ${stop.storeNumber}`)
        .join(' → ');
      
      if (stops) {
        return `${pickup} → ${stops} → ${delivery}`;
      }
    }
    
    return `${pickup} → ${delivery}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FaSpinner className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Predefined Jobs</h1>
            <p className="text-gray-600">Manage reusable job templates for frequent deliveries</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Create Predefined Job
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search predefined jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {totalJobs} predefined job{totalJobs !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Predefined Jobs Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job Details
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Route
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(predefinedJobs) && predefinedJobs.map((job) => {
                // Skip rendering if job is null or doesn't have an id
                if (!job || !job.id) {
                  console.warn('Skipping job with invalid data:', job);
                  return null;
                }
                
                return (
                  <tr key={job.id} className="hover:bg-gray-50">
                    {/* <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{job?.title || 'Untitled'}</div>
                        <div className="text-sm text-gray-500">{job?.description || 'No description'}</div>
                      </div>
                    </td> */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={formatRoute(job)}>
                        {formatRoute(job)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${job?.price ? parseFloat(job.price).toFixed(2) : '0.00'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(job?.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {job?.useCount || 0} time{(job?.useCount || 0) !== 1 ? 's' : ''}
                      </div>
                      {job?.lastUsed && (
                        <div className="text-xs text-gray-500">
                          Last: {formatDate(job.lastUsed)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOpenAssignModal(job)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Assign Job"
                        >
                          <FaUser className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditJob(job)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Job"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteJob(job?.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Job"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {(!Array.isArray(predefinedJobs) || predefinedJobs.length === 0) && (
          <div className="text-center py-12">
            <FaRoute className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No predefined jobs</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first predefined job template.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <FaPlus className="-ml-1 mr-2 h-4 w-4" />
                Create Predefined Job
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Job Modal */}
      {showCreateModal && (
        <JobFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateJob}
          title="Create Predefined Job"
          submitButtonText="Create Job"
          mode="predefined"
        />
      )}

      {showEditModal && selectedJob && selectedJob.id && (
        <JobFormModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedJob(null);
          }}
          onSubmit={handleUpdateJob}
          job={selectedJob}
          title="Edit Predefined Job"
          submitButtonText="Update Job"
          mode="predefined"
        />
      )}

      {/* Assign Job Modal */}
      {showAssignModal && selectedJob && selectedJob.id && (
        <AssignJobModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedJob(null);
          }}
          onAssign={handleAssignJob}
          job={selectedJob}
          availableDrivers={availableDrivers}
          assigningJob={assigningJob}
        />
      )}
    </div>
  );
};

export default AdminPredefinedJobs;
